import type { Language } from '@eformulieren/engine'
import { localeOf } from '../language/text.js'

/**
 * Hoe de deadline in de metaregel van de kop staat: de datum, hoeveel dagen er nog zijn, en of dat
 * aandacht vraagt (R-128, R-78, besluit van 2 november 2026, eisenboek 12.47).
 *
 * **Kalenderdagen in de tijdzone van de lezer, en geen milliseconden gedeeld door een etmaal.** Wie op
 * 30 november om 23:00 kijkt naar een deadline op 1 december om 17:00, heeft er morgen nog en niet
 * "nog 0 dagen". Een rekensom op milliseconden schuift bovendien een dag bij de overgang naar
 * wintertijd. Daarom de datum van nu en de datum van de deadline elk als kalenderdag van de lezer, en
 * het verschil daartussen.
 *
 * **Verstreken is het moment, niet de dag.** Een deadline van vandaag om 17:00 staat er om 16:59 als
 * "vandaag" en om 17:01 als verstreken; dat de deadline het formulier dan alleen-lezen maakt is een
 * apart gedrag (R-130) en hoort hier niet.
 *
 * **Dichtbij is zeven dagen of minder.** Alleen dan krijgt het deadlinedeel de waarschuwingskleur;
 * verstreken krijgt de foutkleur, en anders staat het er neutraal (besluit van 2 november 2026).
 */
export type DeadlineStand = 'ruim' | 'dichtbij' | 'verstreken'

export interface DeadlineText {
  /** "1 december 2026", in de taal van de lezer. */
  readonly datum: string
  /** "nog 29 dagen", "morgen", "vandaag om 17:00", of "verstreken". */
  readonly rest: string
  readonly stand: DeadlineStand
  /** De hele zin, voor een plek die één tekst nodig heeft: "Deadline 1 december 2026, nog 29 dagen". */
  readonly zin: string
}

/** Vanaf hoeveel dagen of minder de deadline de waarschuwingskleur krijgt. */
export const NEAR_DAYS = 7

/** De kalenderdag van een moment in de tijdzone van de browser, als dagnummer sinds 1970. */
function calendarDay(moment: Date): number {
  return Math.round(Date.UTC(moment.getFullYear(), moment.getMonth(), moment.getDate()) / 86_400_000)
}

export function deadlineText(deadline: string, now: Date, language: Language): DeadlineText | undefined {
  const moment = new Date(deadline)
  if (Number.isNaN(moment.getTime()) || Number.isNaN(now.getTime())) return undefined
  const locale = localeOf(language)
  const datum = moment.toLocaleDateString(locale, { day: 'numeric', month: 'long', year: 'numeric' })
  const label = language === 'nl' ? 'Deadline' : 'Deadline'
  if (moment.getTime() <= now.getTime()) {
    const rest = language === 'nl' ? 'verstreken' : 'passed'
    return {
      datum,
      rest,
      stand: 'verstreken',
      zin: language === 'nl' ? `${label} ${datum} is verstreken` : `${label} ${datum} has passed`,
    }
  }
  const days = calendarDay(moment) - calendarDay(now)
  const time = moment.toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' })
  const rest =
    days <= 0
      ? language === 'nl'
        ? `vandaag om ${time}`
        : `today at ${time}`
      : days === 1
        ? language === 'nl'
          ? `morgen om ${time}`
          : `tomorrow at ${time}`
        : language === 'nl'
          ? `nog ${String(days)} dagen`
          : `${String(days)} days left`
  return { datum, rest, stand: days <= NEAR_DAYS ? 'dichtbij' : 'ruim', zin: `${label} ${datum}, ${rest}` }
}
